import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box, Container, Typography, Chip, Stack, Button,
  CircularProgress, Breadcrumbs, Link as MuiLink, GlobalStyles,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock, BarChart3, GraduationCap, Tag, Sparkles, BookOpen, Download, Rocket } from "lucide-react";
import DOMPurify from "dompurify";
import { getCourse } from "../api/courses";
import api from "../api/axios";
import PageShell from "../components/layout/PageShell";
import ContactSection from "../components/shared/ContactSection";

const API_ORIGIN = new URL(api.defaults.baseURL, window.location.origin).origin;
function resolveMediaUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

/* Brand palette taken from the NetLabs ITS logo: royal blue + deep navy.
   Levels vary only in shade, so every course stays on-brand. */
const BRAND = {
  navy: "#0B2A5B",
  royal: "#1E56A8",
  sky: "#38A1E8",
  soft: "#EAF2FC",
};
const THEME = {
  BEGINNER:     { a: "#2E8BE0", b: "#38BDF8", soft: BRAND.soft, hero: `linear-gradient(120deg,${BRAND.navy} 0%,#1E56A8 55%,#38A1E8 100%)` },
  INTERMEDIATE: { a: "#1E56A8", b: "#3B82F6", soft: BRAND.soft, hero: `linear-gradient(120deg,${BRAND.navy} 0%,#1E56A8 60%,#3B82F6 100%)` },
  PROFESSIONAL: { a: "#173F85", b: "#2563EB", soft: BRAND.soft, hero: `linear-gradient(120deg,#061839 0%,${BRAND.navy} 45%,#1E56A8 100%)` },
  ADVANCED:     { a: "#173F85", b: "#2563EB", soft: BRAND.soft, hero: `linear-gradient(120deg,#061839 0%,${BRAND.navy} 45%,#1E56A8 100%)` },
};
const DEFAULT_THEME = THEME.INTERMEDIATE;

const HEAD = '"Bricolage Grotesque", "Segoe UI", sans-serif';
const BODY = '"Figtree", "Segoe UI", sans-serif';

function CourseHeroImage({ src, alt, theme }) {
  const [failed, setFailed] = useState(!src);
  const hasImage = !failed && src;
  return (
    <Box
      sx={{
        width: "100%", borderRadius: "24px", overflow: "hidden", lineHeight: 0,
        // Fixed height only for the icon placeholder; a real image keeps its own proportions
        height: hasImage ? "auto" : { xs: 220, md: 340 },
        background: theme.soft, display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 24px 60px -20px rgba(15,23,42,0.45)", border: "4px solid #fff",
      }}
    >
      {hasImage ? (
        <Box component="img" src={src} alt={alt} onError={() => setFailed(true)}
          sx={{ display: "block", width: "100%", height: "auto" }} />
      ) : (
        <GraduationCap size={72} color={theme.a} strokeWidth={1.4} />
      )}
    </Box>
  );
}

function FormattedDescription({ html, theme }) {
  if (!html) return null;
  const normalizedHtml = html.replace(/&nbsp;/gi, " ").replace(/\u00A0/g, " ");
  const clean = DOMPurify.sanitize(normalizedHtml, {
    ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "h1", "h2", "h3", "h4", "a"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
  if (!clean || clean.replace(/<(.|\n)*?>/g, "").trim() === "") return null;

  return (
    <Box
      sx={{
        fontFamily: BODY, fontSize: 16.5, color: "#334155", lineHeight: 1.9,
        whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "normal",
        "& *": { color: "inherit !important", whiteSpace: "normal !important" },
        "& p": { mb: 2.25, textAlign: "justify", textJustify: "inter-word", hyphens: "auto", WebkitHyphens: "auto" },
        "& h1, & h2, & h3, & h4": {
          fontFamily: HEAD, fontWeight: 800, color: "#0B1530 !important", fontSize: 22,
          mt: 5, mb: 1.75, pl: 2, borderLeft: `5px solid ${theme.a}`, lineHeight: 1.25,
          "&:first-of-type": { mt: 0 },
        },
        "& ul, & ol": { listStyle: "none", p: 0, mb: 3 },
        "& li": {
          position: "relative", mb: 1.25, pl: 4.5, py: 1.25, pr: 2,
          background: theme.soft, borderRadius: "12px",
          "&::before": {
            content: '"✓"', position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            width: 22, height: 22, borderRadius: "50%", color: "#fff", fontSize: 12, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${theme.a}, ${theme.b})`,
          },
        },
        "& strong, & b": { fontWeight: 800, color: "#0B1530 !important" },
        "& a": { color: `${theme.a} !important`, textDecoration: "underline" },
      }}
      lang="en"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

function StatTile({ icon: Icon, label, value, theme }) {
  return (
    <Box
      sx={{
        flex: "1 1 150px", p: 2.25, borderRadius: "18px", color: "#fff",
        background: `linear-gradient(135deg, ${theme.a}, ${theme.b})`,
        boxShadow: `0 14px 30px -14px ${theme.a}`,
      }}
    >
      <Icon size={22} />
      <Typography sx={{ fontFamily: HEAD, fontWeight: 800, fontSize: 22, mt: 1, lineHeight: 1.1 }}>{value}</Typography>
      <Typography sx={{ fontFamily: BODY, fontSize: 13, opacity: 0.9 }}>{label}</Typography>
    </Box>
  );
}

export default function CourseDetailPage() {
  const { slug } = useParams();
  const { data: course, isLoading, isError } = useQuery({
    queryKey: ["course", slug],
    queryFn: () => getCourse(slug).then((r) => r.data),
    enabled: !!slug,
  });

  const theme = (course && THEME[course.level]) || DEFAULT_THEME;

  const crumbLink = { textDecoration: "none", color: "rgba(255,255,255,0.75)", fontSize: 13, fontFamily: BODY };

  return (
    <PageShell>
      <GlobalStyles styles={{
        "@import": "url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@700;800&family=Figtree:wght@400;600;700&display=swap')",
      }} />

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 14 }}><CircularProgress /></Box>
      ) : isError || !course ? (
        <Container maxWidth="lg" sx={{ textAlign: "center", py: 12 }}>
          <Typography sx={{ fontFamily: HEAD, fontWeight: 800, fontSize: "1.5rem", color: "#0B1530", mb: 2 }}>
            We couldn't find this course
          </Typography>
          <MuiLink component={Link} to="/learn"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontWeight: 700, color: "#2563EB", textDecoration: "none" }}>
            <ArrowLeft size={15} /> Back to courses
          </MuiLink>
        </Container>
      ) : (
        <>
          {/* HERO BAND */}
          <Box sx={{ background: theme.hero, position: "relative", overflow: "hidden", pt: { xs: 4, md: 5 }, pb: { xs: 14, md: 18 } }}>
            <Box sx={{ position: "absolute", top: -120, right: -80, width: 380, height: 380, borderRadius: "50%", background: theme.b, opacity: 0.35, filter: "blur(70px)" }} />
            <Box sx={{ position: "absolute", bottom: -140, left: -60, width: 320, height: 320, borderRadius: "50%", background: theme.a, opacity: 0.3, filter: "blur(70px)" }} />
            <Container maxWidth="lg" sx={{ position: "relative" }}>
              <Breadcrumbs sx={{ mb: 3, "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.5)" } }}>
                <MuiLink component={Link} to="/" sx={crumbLink}>Home</MuiLink>
                <MuiLink component={Link} to="/learn" sx={crumbLink}>Courses</MuiLink>
                <Typography sx={{ fontSize: 13, color: "#fff", fontWeight: 700, fontFamily: BODY }}>{course.title}</Typography>
              </Breadcrumbs>

              <Stack direction="row" sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                {course.category && (
                  <Chip icon={<Tag size={14} color="#fff" />} label={course.category}
                    sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, backdropFilter: "blur(6px)" }} />
                )}
                {course.level && (
                  <Chip icon={<Sparkles size={14} color="#0B1530" />} label={course.level}
                    sx={{ bgcolor: "#fff", color: "#0B1530", fontWeight: 800 }} />
                )}
              </Stack>

              <Typography component="h1" sx={{ fontFamily: HEAD, fontWeight: 800, color: "#fff", lineHeight: 1.08, fontSize: { xs: "2.1rem", md: "3.4rem" }, maxWidth: 780, mb: 2 }}>
                {course.title}
              </Typography>
              {course.subtitle && (
                <Typography sx={{ fontFamily: BODY, fontSize: { xs: 16, md: 19 }, color: "rgba(255,255,255,0.88)", lineHeight: 1.65, maxWidth: 640 }}>
                  {course.subtitle}
                </Typography>
              )}
            </Container>
          </Box>

          {/* BODY — overlaps the hero */}
          <Container maxWidth="lg" sx={{ mt: { xs: -9, md: -12 }, pb: { xs: 6, md: 8 }, position: "relative" }}>
            <Box sx={{ display: "grid", gap: { xs: 4, md: 5 }, alignItems: "stretch",
              gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1fr) 340px", lg: "minmax(0,1fr) 380px" } }}>
              <Box sx={{ minWidth: 0, alignSelf: "start" }}>
                <CourseHeroImage src={resolveMediaUrl(course.card_image_url)} alt={course.title} theme={theme} />

                <Stack direction="row" sx={{ gap: 1.75, flexWrap: "wrap", mt: 3 }}>
                  {course.duration_weeks && <StatTile icon={Clock} label="Duration" value={`${course.duration_weeks} weeks`} theme={theme} />}
                  {course.level && <StatTile icon={BarChart3} label="Level" value={course.level} theme={theme} />}
                  {course.category && <StatTile icon={BookOpen} label="Category" value={course.category} theme={theme} />}
                </Stack>

                {course.long_description && (
                  <Box sx={{ mt: 6 }}>
                    <Typography sx={{ fontFamily: HEAD, fontWeight: 800, fontSize: { xs: 26, md: 32 }, color: "#0B1530", mb: 3 }}>
                      What you'll learn in this course
                    </Typography>
                    <FormattedDescription html={course.long_description} theme={theme} />
                  </Box>
                )}

                <MuiLink component={Link} to="/learn"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, mt: 5, fontSize: 14, fontWeight: 700, color: theme.a, textDecoration: "none" }}>
                  <ArrowLeft size={16} /> Back to courses
                </MuiLink>
              </Box>

              {/* SIDEBAR */}
              <Box>
                <Box sx={{ position: { md: "sticky" }, top: 100 }}>
                  <Box sx={{
                    borderRadius: "24px", p: 3.25, mb: 2.5, color: "#fff", position: "relative", overflow: "hidden",
                    background: `linear-gradient(145deg, ${theme.a}, ${theme.b})`,
                    boxShadow: `0 24px 50px -20px ${theme.a}`,
                  }}>
                    <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.18)" }} />
                    <Rocket size={28} />
                    <Typography sx={{ fontFamily: HEAD, fontWeight: 800, fontSize: 24, mt: 1, mb: 0.5 }}>Ready to start?</Typography>
                    <Typography sx={{ fontFamily: BODY, fontSize: 14, opacity: 0.92, mb: 2.5 }}>
                      Reserve your seat or ask us anything before you join.
                    </Typography>
                    <Button fullWidth size="large" component="a" href="#contact"
                      sx={{ borderRadius: "12px", mb: 1.25, textTransform: "none", fontWeight: 800, bgcolor: "#fff", color: "#0B1530", "&:hover": { bgcolor: "#F1F5F9" } }}>
                      Enroll now
                    </Button>
                    <Button fullWidth size="large" component="a" href="#contact" startIcon={<Download size={16} />}
                      sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, color: "#fff", border: "1.5px solid rgba(255,255,255,0.6)", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}>
                      Request brochure
                    </Button>
                  </Box>

                  <Box sx={{ bgcolor: "#fff", borderRadius: "20px", p: 3, border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 10px 30px -18px rgba(15,23,42,0.3)" }}>
                    <Typography sx={{ fontFamily: HEAD, fontSize: 17, fontWeight: 800, color: "#0B1530", mb: 1.5 }}>Course details</Typography>
                    {[
                      ["Level", course.level],
                      ["Duration", course.duration_weeks ? `${course.duration_weeks} weeks` : null],
                      ["Category", course.category],
                      ["Status", course.status],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.2, borderBottom: "1px dashed rgba(15,23,42,0.12)", "&:last-of-type": { borderBottom: 0 } }}>
                        <Typography fontSize={13.5} color="#64748B" fontFamily={BODY}>{label}</Typography>
                        <Typography fontSize={13} fontWeight={800} fontFamily={BODY}
                          sx={{ color: theme.a, bgcolor: theme.soft, px: 1.25, py: 0.4, borderRadius: "8px" }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </>
      )}

      <ContactSection />
    </PageShell>
  );
}